import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { selectPromptAuth, selectUserId } from 'store/auth/selectors';
import { selectHasErrors } from 'store/error/selectors';
import { State } from 'store/types';

type SyncState = Omit<Partial<State>, 'sync'> & Pick<State, 'sync'>;

const selectSync = (state: State) => state.sync;

export const selectSyncDownloadCleanData = createSelector(
  selectSync,
  (sync) => sync.downloadCleanData,
);

export const selectServerTimestamp = createSelector(
  selectSync,
  (sync) => sync.serverTimestamp,
);

export const selectAssetTimestamps = createSelector(
  selectSync,
  (sync) => sync.asset_timestamps,
);

export const selectAssetIdsToDownload = createSelector(
  selectSync,
  (sync) => sync.assetIdsToDownload,
);

export const selectOrganisationTimestamps = createSelector(
  selectSync,
  (sync) => sync.organisation_timestamps,
);

export const selectSyncHasPendingDataSync = (state: SyncState): boolean =>
  state.changes?.assets !== undefined &&
  Object.keys(state.changes.assets).length > 0;

// THUMBNAILS DOWNLOAD

export const selectSyncThumbnailDownloadQueue = createSelector(
  selectSync,
  (sync) => sync.downloadedThumbnailsQueue,
);

export const selectSyncThumbnailDownloadQueueCount = createSelector(
  selectSyncThumbnailDownloadQueue,
  (thumbnails) => Object.keys(thumbnails).length,
);

// FILES DOWNLOAD

export const selectSyncFileDownloadQueue = createSelector(
  selectSync,
  (sync) => sync.downloadedFilesQueue,
);

export const selectSyncFileDownloadQueueCount = createSelector(
  selectSyncFileDownloadQueue,
  (files) => Object.keys(files).length,
);

const selectFilesDownloaded = createSelector(
  selectSync,
  (sync) => sync.downloadedFiles,
);

export const selectFilesDownloadedByAsset = createSelector(
  selectFilesDownloaded,
  (filesDownloaded): Record<string, string[]> =>
    Object.entries(filesDownloaded).reduce<Record<string, string[]>>(
      (acc, [fileId, assetId]) => {
        if (!acc[assetId]) {
          acc[assetId] = [];
        }
        acc[assetId]!.push(fileId);
        return acc;
      },
      {},
    ),
);

// FILES UPLOAD

const selectHasFilesToUpload = createSelector(
  selectSync,
  (sync) => sync.files_upload_queue.length > 0,
);

export const selectSyncFileUploadQueue = createSelector(
  selectSync,
  (sync) => sync.files_upload_queue,
);

export const selectSyncFileUploadQueueCount = createSelector(
  selectSync,
  (sync) => sync.files_upload_queue.length,
);

export const selectNetworkQueueBlocked = createSelector(
  selectSync,
  selectUserId,
  selectPromptAuth,
  (_: State, syncStartTime: number) => syncStartTime,
  (sync, userId, promptAuth, syncStartTime) =>
    promptAuth ||
    !userId ||
    (sync.pauseBgNetwork > 0 && !sync.doManualSync) ||
    sync.skipBgNetworkUntilTime > syncStartTime,
);

export const selectDataSyncBlocked = createSelector(
  selectSync,
  (_: State, syncStartTime: number) => syncStartTime,
  (sync, syncStartTime) =>
    sync.nextBgDataSyncTime > syncStartTime ||
    sync.skipBgDataSyncUntilTime > syncStartTime,
);

export const selectAssetsToRemove = createSelector(
  selectSync,
  (sync) => sync.asset_removal_queue,
);

export const selectSyncStatus = createSelector(
  selectSync,
  (sync) => sync.syncStatus,
);
export const selectSyncErrors = createSelector(
  selectSync,
  (sync) => sync.syncErrors,
);

export const selectLastDataSync = createSelector(
  selectSync,
  (sync) => sync.lastBgDataSyncTime,
);

export const selectHasItemsToSync = createSelector(
  selectHasFilesToUpload,
  selectSyncHasPendingDataSync,
  (hasFiles, hasChanges) => hasFiles || hasChanges,
);

export const selectDownloadedThumbnail = createCachedSelector(
  selectSync,
  (_: State, thumbnailId: string) => thumbnailId,
  (
    sync,
    thumbnailId,
  ): string | { assetId: string; width: number; height: number } | undefined =>
    sync.downloadedThumbnails[thumbnailId],
)((_: State, thumbnailId: string) => thumbnailId);

export const hasStartTimePassedLastUpdateCheckTime = (
  state: SyncState,
  syncStartTime: number,
): boolean => state.sync.nextAppUpdateCheckTime < syncStartTime;

export const selectShouldUploadErrors = createSelector(
  selectSync,
  selectHasErrors,
  (_: State, syncStartTime: number) => syncStartTime,
  (sync, hasErrors, syncStartTime): boolean =>
    hasErrors && sync.skipBgErrorUploadUntilTime < syncStartTime,
);
